import type { Option } from '@neo/blocks/data-table/types'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { settingsService } from 'v2/shared/services/settings-service'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import { getAPIConsoleQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RSubaccount } from 'v2/types/api/settings'
import { logger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'

/** Page size for the paginated selects (useRemoteSelect, offset mode). */
export const SUBACCOUNT_PAGE_SIZE = 50

// The name param does case-insensitive CONTAINS matching (SER-5880) —
// verified July 2026 on both the internal accountservice endpoint and the
// public API (name=3 matches "13"/"23"; name=h matches "Screenshots"/"Hi stella")
export const SUBACCOUNT_SEARCH_PARAM = 'name'

// Rows per request when walking pages for the table filter.
export const FILTER_PAGE_SIZE = 200

// Hard ceiling on how far the filter walks, held at the ceiling the previous
// single-request implementation reached so this fix costs no reachable choices.
// Asking for 10k in ONE request timed out a ~4.8k-subaccount org (SER-6315)
// because core allows the upstream 30s; the same 10k walked in FILTER_PAGE_SIZE
// pages stays well inside it, since the timeout was a per-request limit, not a
// budget for the whole walk.
//
// Do NOT lower this to shorten the walk: the only consumer maps these rows to
// static options with no search or load-more, so anything past the cap is
// unreachable rather than merely slower to reach. The cap exists only to stop a
// 70k+ account (largest on CX ~640k rows) walking forever — those still
// truncate, which is why the filter wants real remote search (SER-6320).
export const FILTER_MAX_SUBACCOUNTS = 10_000

/**
 * Why the walk stopped short of the full list.
 *
 * `failed` is retryable — a page errored and the rest were never fetched.
 * `capped` is not — the account genuinely has more rows than the ceiling, so
 * refetching returns the same prefix and only SER-6320's remote search helps.
 */
export type SubaccountFilterGap = 'failed' | 'capped'

export interface SubaccountFilterResult {
  subaccounts: RSubaccount[]
  /** null when the list is known-complete. */
  gap: SubaccountFilterGap | null
}

export interface AllSubaccountsQueryOptions {
  // The list comes back WITHOUT auth tokens by default: the flows list, the runs pages,
  // the export and the tagging dropdown only need names, and a few thousand live
  // subaccount tokens have no business in those browsers. Only the Start node's
  // credential panel asks for them (it embeds the tagged subaccount's token in the
  // copied snippet). The two variants live under different query keys so a names-only
  // cache entry can never be handed to a caller that needs the tokens.
  withAuthTokens?: boolean
}

export const allSubaccountsQueryKey = ({
  withAuthTokens = false,
}: AllSubaccountsQueryOptions = {}) =>
  withAuthTokens
    ? [...getAPIConsoleQueryKeys().subaccounts, 'filter-list', 'with-auth-tokens']
    : [...getAPIConsoleQueryKeys().subaccounts, 'filter-list']

/**
 * Walks pages up to FILTER_MAX_SUBACCOUNTS. Only the table filters and the
 * flow tagging surfaces use this — every subaccount SELECT pages on demand
 * through useRemoteSelect, so nothing user-blocking waits on this.
 *
 * Partial results resolve rather than throw, so one slow page cannot empty the
 * whole filter (the SER-6315 failure). That makes the shortfall invisible to
 * React Query, which sees a success and neither retries nor sets `error` — so
 * the gap is carried in the RESULT instead, and the hook is responsible for
 * telling the user. Returning a bare array here is what let a transient failure
 * on page N silently drop every later subaccount.
 */
export const fetchSubaccountsForFilter = async ({
  withAuthTokens = false,
}: AllSubaccountsQueryOptions = {}): Promise<SubaccountFilterResult> => {
  const collected: RSubaccount[] = []
  let offset = 0

  while (collected.length < FILTER_MAX_SUBACCOUNTS) {
    let page: RSubaccount[]
    let hasNext: boolean
    try {
      const response = await settingsService.listSubaccountsInternal({
        limit: FILTER_PAGE_SIZE,
        offset,
        // core strips auth_token/new_auth_token from each row when this is set; an older
        // core ignores it, which only means the tokens keep coming as before.
        ...(withAuthTokens ? {} : { exclude_auth_token: true }),
      })
      page = response.data.objects || []
      hasNext = !!response.data.meta?.next
    } catch (error) {
      // Nothing in hand is indistinguishable from a broken filter, so let that
      // throw and take React Query's retry; a partial list is worth keeping.
      if (collected.length === 0) throw error
      logger.warn(`Subaccount filter stopped at ${collected.length} after a failed page`)
      return { subaccounts: collected, gap: 'failed' }
    }

    collected.push(...page)
    // Trust meta.next over page length: a short page is not proof of the end.
    if (!hasNext || page.length === 0) return { subaccounts: collected, gap: null }
    offset += FILTER_PAGE_SIZE
  }

  logger.warn(`Subaccount filter truncated at ${FILTER_MAX_SUBACCOUNTS}`)
  return { subaccounts: collected, gap: 'capped' }
}

/** Complete lists are worth caching for the session; a list cut short by a
 * failed page is not — it resolved successfully, so nothing else would retry
 * it, and the filter would keep serving a prefix as the whole account. A capped
 * list is genuinely all we can get, so it caches normally. */
export const subaccountFilterStaleTime = (gap: SubaccountFilterGap | null | undefined) =>
  gap === 'failed' ? 0 : 5 * 60 * 1000

/** What the user is told when the list is short. A capped list cannot be
 * refetched into completeness, so it must not suggest refreshing. */
export const subaccountGapMessage = (gap: SubaccountFilterGap) =>
  gap === 'failed'
    ? {
        title: 'Subaccount filter is incomplete',
        description: 'Some subaccounts could not be loaded. Refresh to try again.',
      }
    : {
        title: 'Subaccount filter is incomplete',
        description: `Showing the first ${FILTER_MAX_SUBACCOUNTS.toLocaleString()} subaccounts. Search for a specific one if it is missing.`,
      }

export const useAllSubaccountsQuery = (
  enabled = true,
  options: AllSubaccountsQueryOptions = {},
) => {
  const withAuthTokens = !!options.withAuthTokens
  const query = useQuery({
    queryKey: allSubaccountsQueryKey({ withAuthTokens }),
    queryFn: () => fetchSubaccountsForFilter({ withAuthTokens }),
    // Several requests on large accounts, so a complete list is kept for the
    // session rather than re-walked on every remount/window focus.
    staleTime: (q) => subaccountFilterStaleTime(q.state.data?.gap),
    // Never in embed: the list is the partner's customer roster and is not allow-listed.
    enabled: enabled && !isEmbedMode(),
  })

  // Surface failures once here so the consuming filter doesn't silently render
  // an empty subaccount list
  const { error, data } = query
  useEffect(() => {
    if (error) {
      logger.error('Failed to load subaccounts', error)
      errorToast({
        title: 'Failed to load subaccounts',
        description: 'Subaccount lists may be incomplete. Please refresh and try again.',
      })
    }
  }, [error])

  // A short list is indistinguishable from a small account, so say so. This is
  // NOT covered by the error branch above: a partial walk resolves, so React
  // Query reports success and `error` stays undefined.
  const gap = data?.gap
  useEffect(() => {
    if (!gap) return
    errorToast(subaccountGapMessage(gap))
  }, [gap])

  return query
}

// Only an EXPLICIT `enabled: false` counts as disabled — the one rule for every surface
// (filter options, the tagging dropdown, the runtime's own enabled check). The list is
// proxied verbatim from account-service, so an absent/undefined flag must read as
// enabled: treating it as "off" would empty every filter and dropdown the moment that
// payload changed shape.
export const isSubaccountDisabled = (subaccount: RSubaccount): boolean =>
  subaccount.enabled === false

// Filter value selecting rows with NO subaccount tag — they run and bill as the main
// account. Must match phlo-core's MAIN_ACCOUNT_SUB_AUTH_FILTER, which turns it into an
// IS NULL; lowercase so it can never collide with a real auth id (SA + 18 uppercase
// chars). Same sentinel the call/message log filters already send.
export const MAIN_ACCOUNT_FILTER_VALUE = 'main'

// The "Sub Account" filter option list, sourced from useAllSubaccountsQuery and
// filtered to enabled subaccounts only — a disabled subaccount is a NEW-selection
// source (a filter you could newly pick), so it must not be offered here. This is
// purely for FILTER dropdowns; a flow's or run's already-set tag is displayed
// separately and must keep rendering its name even if that subaccount has since
// been disabled.
//
// "Main Account" leads the list so you can isolate the untagged agents/runs, or
// combine it with subaccounts (the backend ORs the two). It is added only when there
// is at least one subaccount to pick: callers use an empty list to mean "this account
// has no subaccounts, hide the filter entirely", and a lone "Main Account" option
// would mean nothing on an account that has no subaccounts at all.
export const toSubaccountFilterOptions = (subaccounts: RSubaccount[] | undefined): Option[] => {
  const enabled = (subaccounts ?? [])
    .filter((subaccount) => !isSubaccountDisabled(subaccount))
    .map((subaccount) => ({ label: subaccount.name, value: subaccount.auth_id }))
  if (!enabled.length) return []
  return [{ label: 'Main Account', value: MAIN_ACCOUNT_FILTER_VALUE }, ...enabled]
}

// auth_id -> name, for DISPLAYING a tag that is already set (an agent row, a run
// row, an export cell). Deliberately keeps disabled subaccounts, unlike
// toSubaccountFilterOptions above: an agent tagged before its subaccount was
// disabled must still show the name it was tagged to. Callers fall back to the
// raw auth_id on a miss so a tagged row never renders as untagged.
export const toSubaccountNames = (subaccounts: RSubaccount[] | undefined): Map<string, string> =>
  new Map((subaccounts ?? []).map((subaccount) => [subaccount.auth_id, subaccount.name]))
